import * as React from "react";
import { useLocation } from "react-router-dom";

// Each value is the trailing URL path segment of a child route registered
// under the `/agents` parent in AgentsRoutes. The string values are
// load-bearing: they are matched against `location.pathname` to identify
// the active tab, and used as `<Link to={...}>` targets to navigate
// between tabs. Renaming a value requires updating the matching
// `<Route path>` in AgentsRoutes.
export enum AgentTab {
  Sessions = "sessions",
}

function deriveActiveTabFromUrl(path: string | undefined) {
  switch (path) {
    case AgentTab.Sessions:
      return AgentTab.Sessions;
    default:
      return AgentTab.Sessions;
  }
}

/**
 * Returns the active agents tab as a `[tab, setTab]` tuple, derived from
 * the trailing segment of the current URL pathname and re-derived on every
 * pathname change.
 *
 * Any segment that does not match an `AgentTab` value resolves to the
 * default tab, mirroring the route tree's index and wildcard redirects.
 *
 * The setter lets callers driving navigation update the highlighted tab
 * synchronously rather than waiting for the next pathname effect. The
 * pathname remains the source of truth and overwrites any value that
 * diverges from it.
 */
export function useActiveAgentTab() {
  const location = useLocation();
  const path = location.pathname.split("/").pop();

  const [activeTab, setActiveTab] = React.useState(
    deriveActiveTabFromUrl(path),
  );

  React.useEffect(() => {
    setActiveTab(deriveActiveTabFromUrl(path));
  }, [path]);

  return [activeTab, setActiveTab] as const;
}
